<template>
    <v-card style="min-height: 400px;">
        <v-card-title>{{ $t('Kutsu käyttäjiä') }}</v-card-title>
        <v-card-text>

            <v-radio-group v-model="selectedLanguage" :label="$t('Käytä viestipohjaa')" class="ma-0 pa-0">
                <v-radio v-for="lang in languages" :key="lang.code" :label="lang.name" :value="lang.code"
                    class="mb-2 mr-2" :disabled="inProgress">
                </v-radio>
            </v-radio-group>



            <template v-if="(selectedLanguage && filter)">
                <p>
                    {{ $t('Kutsuohje') }}
                </p>

                <v-card color="#f1f1f2" class="mb-3">

                    <textarea v-model="message" :disabled="inProgress"
                        style="white-space:pre-line; background-color: transparent; color: #5e5669de; width: 100%; height: 260px; padding: 10px;">

                    </textarea>
                </v-card>


                <div style="max-height: 300px; overflow-y: scroll;">
                    <v-alert type="warning" text>
                        {{ $t('Varmista, että käyttäjien puhelinnumerot ovat oikein ennen viestin lähetystä.') }}
                    </v-alert>
                    <div>
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th style="width: 50px">
                                        <v-icon v-if="!inProgress" @click="(toggle = !toggle)">{{
                                            icons.mdiCheck
                                            }}</v-icon>
                                    </th>
                                    <th>
                                        {{ $t('Nimi') }}
                                    </th>
                                    <th>{{ $t('Puhelin') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="user in users" :key="user.id">
                                    <td>
                                        <template v-if="(inProgress || done)">
                                            <template v-if="user.selected">
                                                <v-progress-circular v-if="(user.sent === null)" indeterminate size="16"
                                                    width="2"></v-progress-circular>
                                                <v-icon v-else-if="user.sent" color="success">{{
                                                    icons.mdiCheck
                                                    }}</v-icon>
                                                <v-icon v-else-if="!user.sent" color="error">{{
                                                    icons.mdiAlert
                                                    }}</v-icon>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <v-simple-checkbox v-model="user.selected"></v-simple-checkbox>
                                        </template>
                                    </td>
                                    <td @click="() => { user.selected = !user.selected }">
                                        {{ user.name }}
                                    </td>
                                    <td>
                                        {{ user.phone }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot v-if="noNewUsers">
                                <tr>
                                    <td colspan="3">

                                        <div class="pt-4 pb-4 pr-0">
                                            {{ $t('Kutsumattomia käyttäjiä ei löytynyt') }}
                                        </div>

                                    </td>
                                </tr>
                            </tfoot>
                        </v-simple-table>
                    </div>
                    <br>
                    <v-btn v-if="(users.length && !inProgress && !done)" color="primary" @click="sendInvitations">
                        {{ $t('Kutsu valitut käyttäjät') }}

                    </v-btn>
                    <v-alert v-else-if="inProgress" text color="info">
                        {{ $t('Lähetys käynnissä, odota...') }}

                    </v-alert>
                    <template v-else-if="done">
                        <v-alert color="info" text>
                            {{ $t('Lähetys on valmis') }}
                        </v-alert>

                        <v-alert v-if="sendErrors.length" color="error" class="mt-2" text>
                            <div v-for="error in sendErrors" :key="error.id" class="mb-2">
                                {{ $t('Kutsu käyttäjälle') }} {{ error.name }} {{ $t('numeroon') }} {{ error.phone }}
                                {{ $t('epäonnistui') }} ({{
                                    error.error
                                }})
                            </div>
                        </v-alert>
                        <v-btn color="primary" @click="refresh">{{ $t('Päivitä lista') }}</v-btn>
                    </template>
                </div>
            </template>
        </v-card-text>
    </v-card>
</template>

<script>

import { ref, onMounted, watch } from '@vue/composition-api';
import adminApi from '@/api/admin';
import { mdiCheck, mdiAlert, mdiRotate3dVariant } from '@mdi/js';
import useAuth from '@/useAuth';
import invitationTexts from '@/assets/invitation-texts';
import { i18n } from '@/plugins/i18n';

export default {
    components: {

    },
    setup() {
        const { tenant } = useAuth();
        const selectedLanguage = ref(null);
        const filters = [
            { 'value': 1, 'text': 'Uudet käyttäjät' },
            { 'value': 2, 'text': 'Ei kirjautuneet käyttäjät' },
        ]
        const sendErrors = ref([]);
        const filter = ref(1);
        const users = ref([]);
        const toggle = ref(false);
        const inProgress = ref(false);
        const done = ref(false);
        const message = ref('');
        const noNewUsers = ref(false);
        const languages = [
            { 'name': 'Suomi', 'code': 'fi' },
            { 'name': 'Svenska', 'code': 'sv' },
            { 'name': 'English', 'code': 'en' },
            { 'name': 'Lietuvių', 'code': 'lt' },
            { 'name': 'Español', 'code': 'es' },
            { 'name': 'Norsk', 'code': 'no' }
        ]

        watch(toggle, () => {
            users.value.forEach(user => {
                user.selected = !user.selected;
            });
        })

        watch(selectedLanguage, () => {
            getMessageTemplate();
        });

        const getMessageTemplate = () => {
            if (filter.value && selectedLanguage.value) {
                if (filter.value == 1)
                    message.value = invitationTexts.invitation[selectedLanguage.value].replaceAll("XXX", tenant.value.name);
                else
                    message.value = invitationTexts.reminder[selectedLanguage.value].replaceAll("XXX", tenant.value.name);
            }
        }

        const onFilterChange = () => {
            if (filter.value === 1) {
                fetchNew();
            }
            else if (filter.value === 2) {
                fetchNotLoggedIn();
            }
        }

        const refresh = () => {
            users.value = [];
            done.value = false;
            fetchNew()
        }

        const fetchNew = async () => {
            noNewUsers.value = false;
            var res = await adminApi.getNewUsers();
            getUsersFromResponse(res.data);
        }

        const fetchNotLoggedIn = async () => {
            var res = await adminApi.getNotLoggedInUsers();
            getUsersFromResponse(res.data);
        }

        const getUsersFromResponse = (usersResponse) => {
            const usersRes = [];
            if (usersResponse.length) {
                usersResponse.forEach(u => {
                    usersRes.push({
                        name: u.last_name + " " + u.first_name,
                        id: u.id,
                        phone: u.phone,
                        selected: false,
                        sending: false,
                        sent: null
                    })
                });
            }
            else {
                noNewUsers.value = true;
            }

            users.value = usersRes;
        }



        const sendInvitations = async () => {
            sendErrors.value = [];
            if (!message.value || !message.value.length) {
                alert(i18n.t("Viesti on pakollinen"));
                return;
            }

            inProgress.value = true;
            let index = 0;
            const selectedUsers = users.value.filter(a => a.selected == true);

            for (let i = 0; i < selectedUsers.length; i++) {
                const res = await adminApi.inviteUser(selectedUsers[i].id, message.value);
                if (res.ok) {
                    selectedUsers[i].sent = true;
                }
                else {
                    selectedUsers[i].sent = false;
                    sendErrors.value.push({
                        id: selectedUsers[i].id,
                        phone: selectedUsers[i].phone,
                        name: selectedUsers[i].name,
                        error: res.error ?? res.data
                    });
                }
            }

            inProgress.value = false;
            done.value = true;
        }

        onMounted(() => {
            fetchNew();
        });

        return {
            filters,
            refresh,
            filter,
            toggle,
            users,
            inProgress,
            languages,
            done,
            sendInvitations,
            selectedLanguage,
            message,
            noNewUsers,
            sendErrors,
            tenant,
            icons: {
                mdiCheck,
                mdiAlert,
                mdiRotate3dVariant
            }
        }
    }
}
</script>

<style></style>